import { useState, useEffect, useCallback, useRef } from 'react';
import { track } from '../services/analyticsTracker';
import {
  QrCode,
  Plus,
  Ban,
  Trash2,
  X,
  User,
  Phone,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Car,
  Search,
  IdCard,
  Link2,
  UserPlus,
  Share2,
  Mail,
  Copy,
  Eye,
  EyeOff,
  Check,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Bell,
} from 'lucide-react';
import { accessApi } from '../services/accessApi';
import { useSmartBack } from '../hooks/useSmartBack';
import { getPostLoginDestinationFromStorage } from '../lib/postLoginDestination';
import { qrImageUrl } from '../hooks/usePersonalQrCode';
import { visitorRegistryAPI } from '../services/api';
import { walkInApi, walkInListFromResponse, WALK_IN_STATUS_BADGE, type WalkIn } from '../services/accessApi';
import { googleMapsDestinationUrl } from '../utils/mapsLink';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Phase 2 (2026-09-12): shared rotating pass renderer for codes with qr_secret.
import RotatingAccessQr from '../components/RotatingAccessQr';
// Guardar pase (ADDENDUM 2026-09-14, Item A): offline visitor-pass PNG save.
import SaveVisitorPass from '../components/SaveVisitorPass';

// Notification-permission nudge (Jaime 2026-10-07): dismiss stored for 7 days.
const NOTIF_PROMPT_DISMISS_KEY = 'vecindus:notif-prompt-dismissed-at';

interface AccessCode {
  id: string;
  code: string;
  type?: string;
  visitorName: string;
  guest_name?: string;
  phone?: string;
  vehicle_plate?: string;
  // 2026-09-15: destination geolocation from GET /qr-access/my-codes
  // (unit geo, else condo geo) — coords-first "Cómo llegar" maps link.
  destination_geo?: { latitude: number; longitude: number } | null;
  // Street address of the destination unit (2026-09-15, Jaime approved showing
  // it on the visitor pass): GET /my-codes enriches rows via
  // destinationAddressForCode (scan-card.js) — "<street>, <unit>".
  destination_address?: string | null;
  validUntil: string;
  valid_until?: string;
  validFrom?: string;
  valid_from?: string;
  // Multi-day range (2026-09-11): optional daily-repeat bounds, 'YYYY-MM-DD' or null.
  // Defensive: rows from backends without the fields arrive as undefined.
  valid_from_date?: string | null;
  valid_until_date?: string | null;
  arrivalAt?: string;
  arrival_at?: string;
  windowHours?: number;
  window_hours?: number;
  // Backend (soft-cancel, migration 111) stamps status='cancelled'; 'revoked'/'expired' kept for legacy shapes.
  // cancelled_at ISO string: alternative soft-cancel marker (code defensively for both).
  cancelled_at?: string | null;
  status: 'active' | 'revoked' | 'expired' | 'cancelled' | string;
  // Phase 2 (2026-09-12): per-code signing secret from my-codes (guest codes
  // get rotating QRs too). Null/absent on older backends -> static QR fallback.
  qr_secret?: string | null;
  createdAt: string;
  created_at?: string;
  email?: string;
}

// Known visitor row from GET /qr-access/visitors (presence-scoped server-side).
interface KnownVisitor {
  visitor_profile_id: string;
  full_name: string;
  phone: string;
  email?: string | null;
  latest_vehicle_plate?: string | null;
  photo_url?: string | null;
  linked?: boolean;
  invite_count?: number;
  last_invited_at?: string;
  id_on_file?: boolean;
}

const emptyForm = {
  guestName: '',
  phone: '',
  email: '',
  arrivalDate: '',
  arrivalTime: '',
  windowHours: 2,
  vehiclePlate: '',
  validFrom: '',
  validUntil: '',
};

// Allowed half-window sizes around the arrival time
const WINDOW_HOURS = [1, 2, 3, 4, 6, 8, 12];

// Shared input styling for the guest-invite card (light + dark theme)
const guestInputCls = [
  'w-full pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm',
  'text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-900',
  'placeholder-slate-400 dark:placeholder-slate-500',
  'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
  '[color-scheme:light] dark:[color-scheme:dark]',
].join(' ');

// Default arrival: today, next half-hour mark
const defaultArrival = () => {
  const d = new Date(Date.now() + 30 * 60 * 1000);
  d.setSeconds(0, 0);
  if (d.getMinutes() < 30) d.setMinutes(30);
  else { d.setHours(d.getHours() + 1); d.setMinutes(0); }
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    arrivalDate: d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate()),
    arrivalTime: pad(d.getHours())+':'+pad(d.getMinutes()),
  };
};

// Share actions block (2026-09-09): the four-channel share row shared by the
// freshly-generated visitor card and the "Compartir de nuevo" panel on active
// guest-code cards (same WhatsApp / Correo / native share with QR image /
// copy message+link flow as the original generated-card actions).
function ShareAccessActions({ onWhatsApp, onEmail, onNative, onCopy, onSendLink, linkBusy, linkAvailable = true }: {
  onWhatsApp: () => void;
  onEmail: () => void;
  onNative: () => void;
  onCopy: () => void;
  // Phase 2 (2026-09-12): the LINK is the credential for guest codes — this
  // action generates (POST /qr-access/codes/:id/invite-link) and opens the
  // share sheet carrying the full link. Optional: hidden when the prop is
  // absent; disabled (not hidden) when the backend answered 404
  // (linkAvailable=false) until the backend deploys.
  onSendLink?: () => void;
  linkBusy?: boolean;
  linkAvailable?: boolean;
}) {
  return (
    <>
      {onSendLink && (
        <div className="mb-2">
          <button
            onClick={onSendLink}
            disabled={linkBusy || linkAvailable === false}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={linkAvailable === false ? 'El enlace de acceso aún no está disponible' : 'Genera y envía el enlace de acceso (el enlace ES el pase)'}
          >
            {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {linkBusy ? 'Generando enlace…' : (linkAvailable === false ? 'Enlace de acceso no disponible' : 'Enviar enlace de acceso')}
          </button>
        </div>
      )}
      <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1">
        <Share2 className="w-3 h-3" />
        Enviar acceso al visitante
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onWhatsApp}
          className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700"
        >
          <Phone className="w-3.5 h-3.5" />
          WhatsApp
        </button>
        <button
          onClick={onEmail}
          className="flex items-center gap-1 px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <Mail className="w-3.5 h-3.5" />
          Correo
        </button>
        <button
          onClick={onNative}
          className="flex items-center gap-1 px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <Share2 className="w-3.5 h-3.5" />
          Compartir…
        </button>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 px-3 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <Copy className="w-3.5 h-3.5" />
          Copiar enlace
        </button>
      </div>
    </>
  );
}

export default function MyQR() {
  const { user, contexts, activeContextId, memberships, ownedUnits, activeCondoId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Back-exit guard (2026-09-22, Jaime bug report): when MyQR is the FIRST
  // history entry (PWA start_url, direct deep link to /my-qr or the
  // /resident-qr alias), hardware/browser back used to exit the site because
  // there was no in-app entry beneath it. Seed one: replace this initial
  // entry with the resolved post-login destination, then push /my-qr back on
  // top. The location.key === 'default' guard keeps normal in-app navigation
  // untouched; both navigates land in the same tick, so nothing renders in
  // between.
  useEffect(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (location.key === 'default' && (typeof idx !== 'number' || idx <= 0)) {
      navigate(getPostLoginDestinationFromStorage(), { replace: true });
      navigate('/home/my-qr');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // FIX 2026-09-16 (live-QA, item 5): users with NO condo membership and NO
  // owned unit (claimed visitors, no-unit signups) land here — show an easy
  // "create your unit for free" CTA (same POST /api/units flow as signup,
  // living in EdicionPerfil > Agregar propiedad).
  const noUnitMode = (memberships || []).length === 0 && (ownedUnits || []).length === 0
    && !['super_admin', 'security', 'empleado'].includes(String(user?.role || ''));
  // Notif-permission nudge: only when 'default' (never activated). 'denied'
  // users are not nagged. Dismissal (button or grant/deny) hides it for 7 days.
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [notifBusy, setNotifBusy] = useState(false);
  useEffect(() => {
    if (typeof window.Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    let dismissedAt = 0;
    try { dismissedAt = Number(localStorage.getItem(NOTIF_PROMPT_DISMISS_KEY) || 0); } catch { /* ignore */ }
    if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    setShowNotifPrompt(true);
  }, []);
  const acceptNotifications = useCallback(async () => {
    setNotifBusy(true);
    try { await Notification.requestPermission(); } catch { /* ignore */ }
    setNotifBusy(false);
    setShowNotifPrompt(false);
    try { localStorage.setItem(NOTIF_PROMPT_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);
  const dismissNotifPrompt = useCallback(() => {
    setShowNotifPrompt(false);
    try { localStorage.setItem(NOTIF_PROMPT_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);
  // Jaime 2026-09-08: employees (backend role 'empleado', work-context
  // employees) must NOT see the "Códigos de invitados" card (generator +
  // active guest codes list). Their personal QR stays visible. Context-aware
  // like AppShell: the active context's role wins, global user.role falls back.
  const activeCtx = contexts.find((c) => c.id === activeContextId);
  const contextRole = activeCtx?.role || user?.role;
  // (2026-09-09, F136) Also treat the condo 'security' membership with
  // sub_type 'empleado' as employee work context — mirrors the backend
  // /qr-access/generate denial (qr-access/routes.js isEmployeeWorkContext /
  // isEmployeeGlobalRole). Their PERSONAL QR stays visible; only the guest
  // generator card hides.
  const isEmployee =
    user?.role === 'empleado' ||
    contextRole === 'empleado' ||
    user?.role === 'security' ||
    (contextRole === 'security' && String(activeCtx?.subType || '') === 'empleado');
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<AccessCode | null>(null);
  // "Compartir de nuevo" (2026-09-09): id of the guest-code card whose share
  // panel is open. The panel reuses the same share flow built for freshly
  // generated visitor QRs, bound to this existing code.
  const [sharingCodeId, setSharingCodeId] = useState<string | null>(null);

  // --- Phase 2 link invites (2026-09-12): the LINK is the credential ---
  // POST /qr-access/codes/:id/invite-link (owner) -> { success, data:{ url } }.
  // Defensive against the parallel backend lane: on 404 (not deployed yet) the
  // action degrades to disabled "no disponible" for that code and the classic
  // QR share stays available. Generated links are cached per code id.
  const [inviteLinks, setInviteLinks] = useState<Record<string, string>>({});
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const [inviteUnavailableIds, setInviteUnavailableIds] = useState<Set<string>>(new Set());

  const getInviteLink = async (code: AccessCode): Promise<string | null> => {
    const cached = inviteLinks[code.id];
    if (cached) return cached;
    if (inviteUnavailableIds.has(code.id)) return null;
    setInviteBusyId(code.id);
    try {
      const res = await accessApi.qrAccess.inviteLink(code.id);
      const data = res.data?.data ?? res.data;
      const url: string | null = data?.url || null;
      if (url) setInviteLinks((prev) => ({ ...prev, [code.id]: url }));
      return url;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Backend contract not deployed yet — disable the action for this code.
        setInviteUnavailableIds((prev) => new Set(prev).add(code.id));
        setSuccessMsg('Enlace no disponible por ahora — comparte el código QR.');
        setTimeout(() => setSuccessMsg(null), 2500);
      } else {
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'No se pudo generar el enlace de acceso');
      }
      return null;
    } finally {
      setInviteBusyId(null);
    }
  };

  // Prominent "Enviar enlace de acceso": generate the link, then open the
  // share sheet (native) / clipboard fallback — ALL channels carry the link;
  // no QR image attachment (the link IS the pass, it never expires in a
  // screenshot and survives a screenshot being forwarded).
  const handleSendInviteLink = async (code: AccessCode) => {
    const url = await getInviteLink(code);
    if (!url) return;
    const text = shareMessage(code, url);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Tu acceso Vecindus', text, url });
        return;
      }
    } catch { /* user cancelled — fall through to copy */ }
    try {
      await navigator.clipboard?.writeText(text);
      setSuccessMsg('Enlace de acceso copiado al portapapeles');
    } catch { setSuccessMsg('No se pudo copiar el enlace'); }
    setTimeout(() => setSuccessMsg(null), 2500);
  };

  // Editar / Extender vigencia (2026-09-11): PATCH /api/qr-access/codes/:id/validity.
  // editingCodeId opens the edit dialog for one row; extendBusyId guards the
  // quick +N h buttons. Optimistic update + rollback on failure (handleExtend).
  const [editingCodeId, setEditingCodeId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ date: '', time: '', windowHours: 2, validFrom: '', validUntil: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [extendBusyId, setExtendBusyId] = useState<string | null>(null);

  // Phase 2 re-invite ("Invitar conocido"): presence-scoped known-visitors
  // picker. selectingVisitor pre-fills the invite form; reinviteByProfile
  // routes the submit through POST /qr-access/reinvite instead of /generate.
  const [showKnown, setShowKnown] = useState(false);
  const [knownVisitors, setKnownVisitors] = useState<KnownVisitor[]>([]);
  const [knownLoading, setKnownLoading] = useState(false);
  const [knownError, setKnownError] = useState<string | null>(null);
  const [knownQuery, setKnownQuery] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState<KnownVisitor | null>(null);
  // Archivados (2026-09-11): codes expired more than 24h ago collapse here.
  const [showArchived, setShowArchived] = useState(false);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accessApi.qrAccess.myCodes();
      const payload = res.data?.data ?? res.data;
      const list = payload?.codes || payload?.data?.codes || (Array.isArray(payload) ? payload : []);
      const normalized = Array.isArray(list) ? list.map((c: any) => ({
        ...c,
        visitorName: c.visitor_name || c.visitorName || c.guest_name || '',
        validUntil: c.valid_until || c.validUntil || '',
        createdAt: c.created_at || c.createdAt || '',
        // 2026-09-11 fix: derive arrival = window midpoint + window_hours =
        // half-span from valid_from/valid_until (symmetric model). Falls back
        // to raw fields only for backends that still echo arrival columns.
        ...(symWindowFor(c) ?? {
          arrivalAt: c.arrival_at || c.arrivalAt || undefined,
          windowHours: c.window_hours ?? c.windowHours ?? undefined,
        }),
        vehicle_plate: c.vehicle_plate || undefined,
        phone: c.visitor_phone || c.phone || undefined,
        status: c.status || 'active',
        valid_from_date: c.valid_from_date ?? null,
        valid_until_date: c.valid_until_date ?? null,
      })) : [];
      // Show only guest codes in the guest section; personal codes are separate
      setCodes(normalized);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar códigos de acceso');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isEmployee) return; // employees have no guest codes to manage
    fetchCodes();
  }, [fetchCodes, isEmployee]);

  // Symmetric-window derivation (2026-09-11 fix, Editar-vigencia no-op bug):
// access_codes stores NO arrival_at/window_hours columns — the validity
// window is fully determined by valid_from..valid_until, with
// arrival = midpoint(valid_from, valid_until) and window_hours = half-span
// (same derivation as the PATCH /codes/:id/validity backend response and
// scan-card.js arrivalWindow()). Rows from /my-codes therefore carry only
// the window edges; never label valid_from as the arrival time.
function symWindowFor(c: { valid_from?: string | null; valid_until?: string | null; validFrom?: string | null; validUntil?: string | null }): { arrivalAt: string; windowHours: number } | null {
  const fromMs = Date.parse(c.valid_from || c.validFrom || '');
  const untilMs = Date.parse(c.valid_until || c.validUntil || '');
  if (Number.isNaN(fromMs) || Number.isNaN(untilMs) || untilMs <= fromMs) return null;
  return {
    arrivalAt: new Date(fromMs + (untilMs - fromMs) / 2).toISOString(),
    windowHours: (untilMs - fromMs) / 2 / 3600000,
  };
}

// Nearest selector option for a (possibly fractional) half-span, e.g. 5.5 -> 6.
function nearestWindowOption(h: number): number {
  return WINDOW_HOURS.reduce((best, o) => (Math.abs(o - h) < Math.abs(best - h) ? o : best), WINDOW_HOURS[0]);
}

// Badge status (2026-09-11 fix): the /qr-access/my-codes endpoint returns raw
// access_codes rows (SELECT c.* in src/database/access-codes.js getByUser), so
// `status` is only 'active' | 'cancelled' (soft-cancel, migration 111) or NULL -
// it never becomes 'expired'. Expiry must be computed client-side from the
// validity end: for arrival-window codes that's arrival_at + window_hours
// (+/- spread around arrival, set at generation); legacy codes fall back to
// valid_until. Cancelled/revoked wins over expiry.
function codeValidityEndIso(c: Pick<AccessCode, 'validUntil' | 'valid_until' | 'arrivalAt' | 'arrival_at' | 'windowHours' | 'window_hours'>): string {
  const wh = c.windowHours ?? c.window_hours;
  const arr = c.arrivalAt || c.arrival_at;
  return arr && wh != null && !Number.isNaN(new Date(arr).getTime())
    ? new Date(new Date(arr).getTime() + wh * 3600 * 1000).toISOString()
    : (c.validUntil || c.valid_until || '');
}

// Window start (2026-09-11): arrival - window_hours, same window computation
// as codeValidityEndIso (which computes arrival + window_hours). Legacy codes
// without an arrival/window pair have no start and are treated as live.
function codeWindowStartIso(c: Pick<AccessCode, 'validUntil' | 'valid_until' | 'arrivalAt' | 'arrival_at' | 'windowHours' | 'window_hours'>): string {
  const wh = c.windowHours ?? c.window_hours;
  const arr = c.arrivalAt || c.arrival_at;
  return arr && wh != null && !Number.isNaN(new Date(arr).getTime())
    ? new Date(new Date(arr).getTime() - wh * 3600 * 1000).toISOString()
    : '';
}

// Badge status (2026-09-11, 4-state ruling): 
// - 'revoked'  -> "Cancelado": cancelled/revoked by user (wins over all others).
// - 'expired'  -> "Expirado": now is after the window end.
// - 'pending'  -> "Vigente": code exists, not cancelled, but the access window
//   (arrival - window_hours) has not opened yet (future arrival).
// - 'active'   -> "Permiso Activo": now is inside the window.
// 'pending' only applies to arrival-window codes; legacy valid_until-only codes
// with a future end are 'active'.
function effectiveCodeStatus(c: Pick<AccessCode, 'status' | 'cancelled_at' | 'validUntil' | 'valid_until' | 'arrivalAt' | 'arrival_at' | 'windowHours' | 'window_hours'>): 'active' | 'pending' | 'revoked' | 'expired' {
  const raw = c.status;
  if (raw === 'cancelled' || raw === 'revoked') return 'revoked';
  // Soft-cancel defensive mapping: a non-empty cancelled_at ISO stamp also means revoked,
  // even if the status field was not updated to 'cancelled'.
  if (c.cancelled_at) return 'revoked';
  // Archivo (2026-09-11): same computation is reused for the archived-codes
  // split below via codeValidityEndIso (arrival_at + window_hours, fallback valid_until).
  const endIso = codeValidityEndIso(c);
  if (endIso && !Number.isNaN(new Date(endIso).getTime()) && new Date(endIso).getTime() < Date.now()) {
    return 'expired';
  }
  const startIso = codeWindowStartIso(c);
  if (startIso && !Number.isNaN(new Date(startIso).getTime()) && new Date(startIso).getTime() > Date.now()) {
    return 'pending';
  }
  return 'active';
}

// Multi-day daily-repeat line (2026-09-11): "Válido {from} a {until}, {HH:mm} ±{N}h
// cada día" when the code has valid_from_date / valid_until_date. Defensive:
// returns '' when the backend does not send the range fields.
function dailyRangeLine(c: Pick<AccessCode, 'valid_from_date' | 'valid_until_date' | 'arrivalAt' | 'arrival_at' | 'windowHours' | 'window_hours'>): string {
  const from = c.valid_from_date || '';
  const until = c.valid_until_date || '';
  if (!from && !until) return '';
  const arr = c.arrivalAt || c.arrival_at;
  const wh = c.windowHours ?? c.window_hours;
  const arrivalPart = arr && !Number.isNaN(new Date(arr).getTime())
    ? new Date(arr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    : '';
  const windowPart = wh != null ? ` ±${wh}h cada día` : '';
  const range = from && until ? `${from} a ${until}` : (from || until);
  return `Válido ${range}${arrivalPart ? `, ${arrivalPart}` : ''}${windowPart}`;
}

// Optimistic +N h shift for the quick extend buttons (2026-09-11): SYMMETRIC
// growth to match the backend extendHours ruling (updated 2026-09-11) — the
// window grows `hours` on EACH side (start -N h, end +N h), so the arrival
// midpoint is unchanged and window_hours += N. Legacy codes without an
// arrival shift valid_until instead. Rolled back on request failure.
function shiftCodeWindow(c: AccessCode, hours: number): AccessCode {
  const arr = c.arrivalAt || c.arrival_at;
  const wh = c.windowHours ?? c.window_hours;
  if (arr && !Number.isNaN(new Date(arr).getTime())) {
    // 2026-09-11 (Jaime ruling): symmetric extend — valid_from -= N h,
    // valid_until += N h; arrival midpoint unchanged, window_hours += N.
    const whNum = wh ?? 0;
    const arrMs = new Date(arr).getTime();
    const newWh = whNum + hours;
    const fromIso = new Date(arrMs - newWh * 3600 * 1000).toISOString();
    const untilIso = new Date(arrMs + newWh * 3600 * 1000).toISOString();
    return {
      ...c,
      windowHours: wh != null ? newWh : undefined,
      window_hours: wh != null ? newWh : undefined,
      validFrom: fromIso,
      valid_from: fromIso,
      validUntil: untilIso,
      valid_until: untilIso,
    };
  }
  const until = c.validUntil || c.valid_until || '';
  if (until && !Number.isNaN(new Date(until).getTime())) {
    const untilMs = new Date(until).getTime();
    const fromMs = c.validFrom || c.valid_from ? new Date(c.validFrom || c.valid_from!).getTime() : untilMs - 3600 * 1000;
    const iso = new Date(untilMs + hours * 3600 * 1000).toISOString();
    const fromIso = new Date(fromMs - hours * 3600 * 1000).toISOString();
    return { ...c, validFrom: fromIso, valid_from: fromIso, validUntil: iso, valid_until: iso };
  }
  return c;
}

// Condo resolution shared by generate + re-invite (same pattern as before).
  const resolveCondoId = () => {
    const active = contexts.find(c => c.id === activeContextId);
    const managed = contexts.find(c => c.kind === 'condo');
    // D6 fix (2026-10-01, QA): the condo being MANAGED (activeCondoId from the
    // condo switcher) wins over the active-context condo — a super-admin
    // managing condo B must not write guest codes into their own active condo.
    return activeCondoId || managed?.condoId || active?.condoId || user?.condoId;
  };

  const loadKnownVisitors = useCallback(async () => {
    setKnownLoading(true);
    setKnownError(null);
    try {
      const condoId = resolveCondoId();
      if (!condoId) throw new Error('No se encontró un condominio activo.');
      // Privacy: backend scopes to visitors already invited to THIS condo only.
      const res = await visitorRegistryAPI.listKnown(condoId);
      const payload = res.data?.data ?? res.data;
      const list = payload?.visitors || (Array.isArray(payload) ? payload : []);
      setKnownVisitors(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setKnownError(err?.response?.data?.error || err?.message || 'Error al cargar visitantes conocidos');
      setKnownVisitors([]);
    } finally {
      setKnownLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexts, activeContextId, activeCondoId, user?.condoId]);

  const openKnownPicker = () => {
    setShowKnown(true);
    loadKnownVisitors();
  };

  const pickKnownVisitor = (v: KnownVisitor) => {
    const arr = defaultArrival();
    setSelectedVisitor(v);
    // BUG 2 (Jaime QA round 3, 2026-09-15): re-invite prefill now carries the
    // visitor's prior email (visitor_profiles.email) and last-used plate
    // (latest access_codes.vehicle_plate) from the known-visitors GET.
    setForm({ ...emptyForm, guestName: v.full_name, phone: v.phone || '', email: v.email || '', vehiclePlate: v.latest_vehicle_plate || '', arrivalDate: arr.arrivalDate, arrivalTime: arr.arrivalTime, windowHours: 2 });
    setShowKnown(false);
    setShowForm(true);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.guestName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      track('qr_code_generated');
      // Backend requires condoId — resolve from the active context (same
      // pattern as StaffDirectory).
      const condoId = resolveCondoId();
      if (!condoId) {
        setError('No se encontró un condominio activo. Selecciona un condominio e intenta de nuevo.');
        setSubmitting(false);
        return;
      }
      // D6 fix (2026-10-01, QA), ambiguity guard: when several condos are
      // manageable and none is explicitly selected, refuse to guess the write
      // target instead of silently defaulting.
      const condoCandidates = new Set<string>([
        ...contexts.filter((c) => c.kind === 'condo' && c.condoId).map((c) => c.condoId as string),
        ...memberships.map((mm) => mm.condoId).filter(Boolean),
      ]);
      if (condoCandidates.size > 1 && !activeCondoId) {
        setError('Hay varios condominios disponibles. Selecciona cuál vas a administrar antes de generar códigos.');
        setSubmitting(false);
        return;
      }
      if (!form.arrivalDate || !form.arrivalTime) {
        setError('Indica la fecha y hora de llegada del visitante.');
        setSubmitting(false);
        return;
      }
      // Arrival window: guest arrives at arrivalAt; code valid within +/- windowHours.
      const arrivalAt = new Date(form.arrivalDate+'T'+form.arrivalTime);
      if (Number.isNaN(arrivalAt.getTime())) {
        setError('Fecha u hora de llegada inválida.');
        setSubmitting(false);
        return;
      }
      if (form.validFrom && form.validUntil && form.validUntil < form.validFrom) {
        setError('El rango de días es inválido: "hasta" no puede ser anterior a "desde".');
        setSubmitting(false);
        return;
      }
      const windowHours = form.windowHours;
      // Legacy fallback for backends without arrival_at/window_hours:
      // valid_until = arrival + window (keeps current API contract working).
      const validUntil = new Date(arrivalAt.getTime() + windowHours * 3600 * 1000).toISOString();
      // Backend expects { condoId, type, visitorName, visitorPhone, validUntil }.
      // New arrival-window fields are sent alongside legacy valid_hours/valid_until fallbacks.
      // Re-invite fast path (plan task 9): a selected known visitor goes
      // through POST /qr-access/reinvite (backend enforces the presence row +
      // fills name/phone from the registry profile).
      const res = selectedVisitor
        ? await visitorRegistryAPI.reinvite({
            condoId,
            visitorProfileId: selectedVisitor.visitor_profile_id,
            arrivalAt: arrivalAt.toISOString(),
            windowHours,
            vehiclePlate: form.vehiclePlate.trim() || undefined,
            visitorEmail: form.email.trim() || undefined,
          })
        : await accessApi.qrAccess.generate({
        condoId,
        type: 'guest',
        visitorName: form.guestName.trim(),
        visitorPhone: form.phone.trim() || undefined,
        vehiclePlate: form.vehiclePlate.trim() || undefined,
        visitorEmail: form.email.trim() || undefined,
        arrivalAt: arrivalAt.toISOString(),
        // Multi-day daily-repeat range (2026-09-11): optional 'YYYY-MM-DD'
        // bounds. Omitted entirely when empty (single-visit code).
        ...(form.validFrom ? { validFrom: form.validFrom } : {}),
        ...(form.validUntil ? { validUntil: form.validUntil } : {}),
        arrival_at: arrivalAt.toISOString(),
        windowHours,
        window_hours: windowHours,
        validHours: windowHours,
        valid_hours: windowHours,
        validUntil,
        valid_until: validUntil,
      } as any);
      // Envelope: { success, data: { code: <access_codes row> } }
      const data = res.data?.data ?? res.data;
      const codeRow = data?.code ?? data;
      const newCode: AccessCode = {
        id: codeRow?.id || '',
        code: codeRow?.code || '',
        type: codeRow?.type || 'guest',
        visitorName: codeRow?.visitor_name || form.guestName.trim(),
        validUntil: codeRow?.valid_until || validUntil,
        createdAt: codeRow?.created_at || new Date().toISOString(),
        vehicle_plate: codeRow?.vehicle_plate || form.vehiclePlate.trim() || undefined,
        destination_geo: codeRow?.destination_geo ?? null,
        arrivalAt: arrivalAt.toISOString(),
        windowHours,
        valid_from_date: codeRow?.valid_from_date ?? form.validFrom ?? null,
        valid_until_date: codeRow?.valid_until_date ?? form.validUntil ?? null,
        status: 'active',
        // Phase 2 (2026-09-12): rotating guest pass — generate response now
        // echoes qr_secret for guest codes; null on older backends.
        qr_secret: codeRow?.qr_secret ?? null,
        // Kept for the share actions below (WhatsApp / email prefill).
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
      };
      setGeneratedCode(newCode);
      setSuccessMsg('Código QR generado correctamente');
      setForm(emptyForm);
      setSelectedVisitor(null);
      setShowForm(false);
      fetchCodes();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al generar código');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Share actions for generated/existing visitor QRs (2026-09-09) ---
  // Code-parametric so the SAME flow serves the freshly-generated card and
  // the "Compartir de nuevo" panel on active guest-code cards.
  // Registration link (same one shown on the card) + code details.
  // Destination label (2026-09-11, Jaime): /qr-access/my-codes returns raw
  // access_codes rows (SELECT c.*) with NO condo/unit/address fields, and the
  // condo address_data is not exposed by any endpoint this page consumes.
  // Use the client-side membership data instead (AuthContext): condo name +
  // unit label of the active condo context. Falls back to the context label.
  const destinationLabel = (() => {
    const condoId = activeCtx?.condoId || user?.condoId;
    const m = memberships.find((mm) => mm.condoId === condoId)
      || memberships.find((mm) => mm.condoId === (contexts.find((c) => c.kind === 'condo')?.condoId));
    const parts = m
      ? [m.condoName, m.unitLabel || m.unitNumber]
      : [activeCtx?.label];
    return parts.filter(Boolean).join(' — ');
  })();

  // WhatsApp share coords fix (2026-09-15 live QA): POST /qr-access/generate
  // does NOT echo destination_geo (only GET /my-codes enriches rows with it),
  // so a freshly-generated code shared immediately fell back to the label
  // query ("Buena Vista — TH1" -> wrong place in Google Maps). Merge the
  // enriched my-codes row's geo by id at share time; label stays the fallback
  // when neither the code row nor my-codes carries geolocation.
  const destinationGeoFor = (code: AccessCode | null): { latitude: number; longitude: number } | null => (
    code?.destination_geo
      ?? codes.find((c) => c.id === code?.id)?.destination_geo
      ?? null
  );

  // Destination address (2026-09-15, Jaime approved): my-codes rows carry
  // destination_address ("<street>, <unit>"); POST /generate does not echo it,
  // so merge the enriched row by id like destinationGeoFor. Empty on older
  // backends -> callers fall back to the condo label.
  const destinationAddressFor = (code: AccessCode | null): string => (
    (code?.destination_address
      ?? codes.find((c) => c.id === code?.id)?.destination_address
      ?? '') || ''
  );

  // "Destino" line (2026-09-15): prefer the unit's street address (includes
  // the unit number, e.g. "Av. Siempreviva 42, TH1"); fall back to the
  // condo+unit label when no address is available.
  const destinationLineFor = (code: AccessCode | null): string => {
    const addr = destinationAddressFor(code);
    return addr ? `Destino: ${addr}` : (destinationLabel ? `Destino: ${destinationLabel}` : '');
  };

  // Google Maps URL for this code's destination (coords-first, label fallback)
  // — used as the on-screen link and encoded as the QR inside the saved pass.
  const destinationMapsUrlFor = (code: AccessCode | null): string => {
    const geo = destinationGeoFor(code);
    return googleMapsDestinationUrl({
      latitude: geo?.latitude,
      longitude: geo?.longitude,
      label: destinationLabel,
    });
  };

  // Offline visitor pass (ADDENDUM 2026-09-14, Item A): text-block lines for
  // the saved PNG — host, destination, validity (multi-day daily line, else
  // arrival±window, else legacy valid_until). Same fields as the share text.
  const passLinesFor = (code: AccessCode): string[] => {
    const daily = dailyRangeLine(code);
    const startIso = codeWindowStartIso(code);
    const endIso = codeValidityEndIso(code);
    const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '');
    const validLine = daily
      ? daily
      : startIso && endIso
        ? `Vigencia: de ${fmt(startIso)} a ${fmt(endIso)}`
        : (endIso ? `Válido hasta: ${fmt(endIso)}` : '');
    // Coords-first (2026-09-15 live QA): the saved PNG can't carry a clickable
    // link — the exact coordinates are printed AND the maps URL is embedded as
    // a small scannable QR (pass.mapsUrl) so Google Maps opens the right pin.
    const geo = destinationGeoFor(code);
    const coords = geo && Number.isFinite(geo.latitude) && Number.isFinite(geo.longitude)
      ? `${geo.latitude}, ${geo.longitude}`
      : '';
    return [
      user?.name ? `Anfitrión: ${user.name}` : '',
      destinationLineFor(code),
      coords ? `Ubicación: ${coords}` : '',
      validLine,
      code.vehicle_plate ? `Placa: ${code.vehicle_plate}` : '',
    ].filter(Boolean);
  };

  // Share text (2026-09-11, Jaime): now includes the host (the resident who
  // creates the code — every row in my-codes belongs to the current user, so
  // user.name is the host), the destination (condo + unit from memberships),
  // and the full validity window when the arrival-window fields exist
  // (start = arrival - window_hours, end = arrival + window_hours; legacy
  // codes keep "Válido hasta" from valid_until).
  // Phase 2 (2026-09-12): when an invite link exists, the LINK is the
  // credential — the message leads with "Entra con este enlace: <url>" and the
  // static code line stays only as a fallback for guards without connectivity.
  const shareMessage = (code: AccessCode | null, inviteUrl?: string | null) => {
    if (!code) return '';
    const startIso = codeWindowStartIso(code);
    const endIso = codeValidityEndIso(code);
    const fmt = (iso: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '');
    const start = fmt(startIso);
    const end = fmt(endIso);
    // Multi-day daily-repeat codes (2026-09-11): concise Spanish line.
    const daily = dailyRangeLine(code);
    const validLine = daily
      ? daily
      : (start && end
        ? `Vigencia: de ${start} a ${end}`
        : (end ? `Válido hasta: ${end}` : ''));
    const host = user?.name || '';
    const lines = [
      `Hola ${code.visitorName || ''}! Te comparto tu acceso a Vecindus.`,
      inviteUrl ? `Entra con este enlace: ${inviteUrl}` : '',
      host ? `Anfitrión: ${host}` : '',
      destinationLabel ? `Destino: ${destinationLabel}` : '',
      // Maps link (Jaime 2026-09-15): coordinate-first — a label query like
      // "Buena Vista — TH1" returned wrong places in live QA. Uses the code's
      // destination_geo (unit/condo stored coords) when present; label search
      // only as fallback.
      (() => {
        const geo = destinationGeoFor(code);
        const mapsLink = googleMapsDestinationUrl({
          latitude: geo?.latitude,
          longitude: geo?.longitude,
          label: destinationLabel,
        });
        return mapsLink ? `Cómo llegar: ${mapsLink}` : '';
      })(),
      inviteUrl ? '' : `Código de acceso: ${code.code || '—'}`,
      validLine,
    ];
    return lines.filter(Boolean).join('\n');
  };

  // 2026-09-09 (Jaime live testing): include the QR IMAGE in the share where
  // the platform supports files. The QR is rendered from qrImageUrl(); we fetch
  // it, convert to a blob → File (mobile Chrome needs real File objects for
  // navigator.share({files})). If fetch fails (no CORS headers) or the browser
  // can't share files, we degrade gracefully to the text+link share that
  // already works — the QR is also rendered inline in this card, so the
  // visitor can always screenshot it.
  const buildQrFile = async (code: AccessCode | null): Promise<File | null> => {
    if (!code?.code) return null;
    try {
      const res = await fetch(qrImageUrl(code.code, 300));
      if (!res.ok) return null;
      const blob = await res.blob();
      return new File([blob], 'qr-acceso-vecindus.png', { type: blob.type || 'image/png' });
    } catch {
      return null;
    }
  };

  const handleNativeShare = async (code: AccessCode | null) => {
    // Phase 2 (2026-09-12): for guest codes the LINK is the credential — if a
    // link is available (cached or freshly generated), native share carries the
    // URL and we DROP the QR image attachment. The QR-image path is kept only
    // as the fallback when the invite-link contract is not live (404).
    const link = code ? await getInviteLink(code) : null;
    const text = shareMessage(code, link);
    if (!text) { setSuccessMsg('Nada que compartir'); return; }
    if (link) {
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Tu acceso Vecindus', text, url: link });
          return;
        }
      } catch { /* user cancelled — fall through to copy */ }
      try {
        await navigator.clipboard?.writeText(text);
        setSuccessMsg('Enlace de acceso copiado al portapapeles');
      } catch { setSuccessMsg('No se pudo copiar el enlace'); }
      setTimeout(() => setSuccessMsg(null), 2500);
      return;
    }
    try {
      if (navigator.share) {
        // Fallback (no link available): attach the QR image file when the
        // platform supports files; else text+url only.
        // Senior review 2026-09-15: the fallback share URL is the invite
        // LINK when we have one; omit entirely otherwise (never /register).
        const base: ShareData = { title: 'Tu acceso Vecindus', text };
        let shareData: ShareData = base;
        const file = await buildQrFile(code);
        if (file && typeof navigator.canShare === 'function') {
          const withFiles = { ...base, files: [file] };
          if (navigator.canShare(withFiles)) shareData = withFiles;
        }
        await navigator.share(shareData);
      } else {
        await navigator.clipboard?.writeText(text);
        setSuccessMsg('Acceso copiado al portapapeles');
        setTimeout(() => setSuccessMsg(null), 2500);
      }
    } catch { /* user cancelled or share failed — fall through to copy */ }
  };

  // Issue 1 fix (2026-09-15): WhatsApp / Correo / Copiar previously used ONLY
  // the per-code cache — unless the host had pressed "Enviar enlace de acceso"
  // first, the share text fell back to the generic /register link. Now all
  // three channels generate/reuse the single-use invite link (POST
  // /qr-access/codes/:id/invite-link, cached per code) BEFORE building the
  // share text, so the invite URL (/invite/:token) always leads the message.
  // Errors are surfaced by getInviteLink and degrade to the classic text.
  const handleWhatsAppShare = async (code: AccessCode | null) => {
    const digits = (code?.phone || '').replace(/\D/g, '');
    const link = code ? await getInviteLink(code) : null;
    const url = digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(shareMessage(code, link))}`
      : `https://wa.me/?text=${encodeURIComponent(shareMessage(code, link))}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleEmailShare = async (code: AccessCode | null) => {
    const subject = 'Tu acceso Vecindus';
    const to = code?.email || '';
    const link = code ? await getInviteLink(code) : null;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareMessage(code, link))}`;
  };

  const handleCopyShareLink = async (code: AccessCode | null) => {
    try {
      const link = code ? await getInviteLink(code) : null;
      await navigator.clipboard?.writeText(shareMessage(code, link));
      setSuccessMsg('Enlace y código copiados');
    } catch {
      setSuccessMsg('No se pudo copiar');
    }
    setTimeout(() => setSuccessMsg(null), 2000);
  };

  // "Ver tarjeta" (2026-09-11, Jaime): re-opens the same freshly-generated
  // card for an EXISTING code so the QR + details can be seen again after it
  // scrolled away / the page reloaded. Reuses the generatedCode state and the
  // same card render (the QR is rendered client-side from the code string via
  // qrImageUrl, so no server image URL is needed). Only offered for
  // active/pending rows; hidden for expired/cancelled (current behavior).
  const openGeneratedCard = (code: AccessCode) => {
    setSharingCodeId(null);
    setGeneratedCode(code);
    setSuccessMsg('Tarjeta del código restaurada');
    setTimeout(() => setSuccessMsg(null), 2500);
  };

  const handleRevoke = async (id: string, opts?: { confirmText?: string; successText?: string }) => {
    if (!confirm(opts?.confirmText || '¿Revocar este código de acceso?')) return;
    try {
      await accessApi.qrAccess.revoke(id);
      setCodes((prev) => prev.filter((c) => c.id !== id));
      if (generatedCode?.id === id) setGeneratedCode(null);
      if (sharingCodeId === id) setSharingCodeId(null);
      setSuccessMsg(opts?.successText || 'Código revocado');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error al revocar código');
    }
  };

  // PATCH response -> row update (2026-09-11). Defensive: when the response
  // envelope lacks data or the validity fields are undefined (older backend),
  // fall back to a full refetch so the row never shows stale values.
  const applyValidityResponse = (id: string, d: any) => {
    const hasFields = d && (d.arrival_at !== undefined || d.window_hours !== undefined || d.valid_from_date !== undefined || d.valid_until_date !== undefined || d.status !== undefined);
    if (!hasFields) { fetchCodes(); return; }
    setCodes((prev) => prev.map((c) => c.id === id ? {
      ...c,
      arrivalAt: d.arrival_at ?? c.arrivalAt,
      arrival_at: d.arrival_at ?? c.arrival_at,
      windowHours: d.window_hours ?? c.windowHours,
      window_hours: d.window_hours ?? c.window_hours,
      validFrom: d.valid_from ?? c.validFrom,
      valid_from: d.valid_from ?? c.valid_from,
      validUntil: d.valid_until ?? c.validUntil,
      valid_until: d.valid_until ?? c.valid_until,
      valid_from_date: d.valid_from_date !== undefined ? d.valid_from_date : c.valid_from_date,
      valid_until_date: d.valid_until_date !== undefined ? d.valid_until_date : c.valid_until_date,
      status: d.status ?? c.status,
      cancelled_at: null,
    } : c));
  };

  // Extender (2026-09-11, Jaime): quick +N h buttons. Active, Vigente (pending)
  // and Expirado rows (revive). Optimistic shift + rollback on failure.
  const handleExtend = async (code: AccessCode, hours: number) => {
    const snapshot = codes;
    setCodes((cs) => cs.map((c) => c.id === code.id ? shiftCodeWindow(c, hours) : c));
    setExtendBusyId(code.id);
    try {
      const res = await accessApi.qrAccess.patchValidity(code.id, { extendHours: hours });
      applyValidityResponse(code.id, res.data?.data ?? res.data);
      setSuccessMsg(`Acceso extendido +${hours} h`);
      setTimeout(() => setSuccessMsg(null), 2500);
    } catch (err: any) {
      setCodes(snapshot); // rollback to pre-optimistic rows
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al extender el código');
    } finally {
      setExtendBusyId(null);
    }
  };

  // Editar (2026-09-11, fixed prefill): derive arrival = midpoint of
  // valid_from..valid_until (shown in device-local/VET time via Date) and
  // windowHours = half-span snapped to the nearest selector option;
  // windowless legacy codes default to 1. Prefilling valid_from as "Llegada"
  // (old behavior) made a 12h window display as "02:30 ± 2" and saved a
  // wrong, re-centered window.
  const openEditDialog = (code: AccessCode) => {
    const sym = symWindowFor(code);
    const arr = sym?.arrivalAt || code.arrivalAt || code.arrival_at || '';
    let date = '', time = '';
    if (arr && !Number.isNaN(new Date(arr).getTime())) {
      const d = new Date(arr);
      const pad = (x: number) => String(x).padStart(2, '0');
      date = d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate());
      time = pad(d.getHours())+':'+pad(d.getMinutes());
    }
    setEditForm({
      date,
      time,
      windowHours: sym ? nearestWindowOption(sym.windowHours) : (code.windowHours ?? code.window_hours ?? 1),
      validFrom: code.valid_from_date || '',
      validUntil: code.valid_until_date || '',
    });
    setEditingCodeId(code.id);
  };

  // Editar submit: PATCH full validity { arrivalAt, windowHours, validFrom, validUntil }.
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = editingCodeId;
    if (!id) return;
    if (!editForm.date || !editForm.time) { setError('Indica la fecha y hora de llegada del visitante.'); return; }
    if (editForm.validFrom && editForm.validUntil && editForm.validUntil < editForm.validFrom) {
      setError('El rango de días es inválido: "hasta" no puede ser anterior a "desde".');
      return;
    }
    const arrivalAt = new Date(editForm.date+'T'+editForm.time);
    if (Number.isNaN(arrivalAt.getTime())) { setError('Fecha u hora de llegada inválida.'); return; }
    setEditSubmitting(true);
    setError(null);
    try {
      const res = await accessApi.qrAccess.patchValidity(id, {
        arrivalAt: arrivalAt.toISOString(),
        windowHours: editForm.windowHours,
        validFrom: editForm.validFrom || null,
        validUntil: editForm.validUntil || null,
      });
      const d = res.data?.data ?? res.data;
      applyValidityResponse(id, d);
      // Keep the restored generated card in sync when editing that same code.
      setGeneratedCode((g) => g && g.id === id ? {
        ...g,
        arrivalAt: d?.arrival_at ?? arrivalAt.toISOString(),
        windowHours: d?.window_hours ?? editForm.windowHours,
        validFrom: d?.valid_from ?? new Date(arrivalAt.getTime() - editForm.windowHours * 3600 * 1000).toISOString(),
        valid_from: d?.valid_from ?? new Date(arrivalAt.getTime() - editForm.windowHours * 3600 * 1000).toISOString(),
        validUntil: d?.valid_until ?? new Date(arrivalAt.getTime() + editForm.windowHours * 3600 * 1000).toISOString(),
        valid_until: d?.valid_until ?? new Date(arrivalAt.getTime() + editForm.windowHours * 3600 * 1000).toISOString(),
        valid_from_date: d?.valid_from_date !== undefined ? d.valid_from_date : (editForm.validFrom || null),
        valid_until_date: d?.valid_until_date !== undefined ? d.valid_until_date : (editForm.validUntil || null),
      } : g);
      setEditingCodeId(null);
      setSuccessMsg('Vigencia actualizada');
      setTimeout(() => setSuccessMsg(null), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Error al actualizar la vigencia');
    } finally {
      setEditSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // "Mi pase" VQR2 (ADDENDUM 2026-09-14b Phase 2 FE): ONE user-identity rotating
  // pass. Backend GET /qr-access/my-pass serves the per-USER qr_secret; the app
  // derives VQR2:<user_id>.<step>.<sig> locally (same 60s step / ±1 grace). The
  // pass carries NO condo — the guard's scanner condo resolves what it grants
  // (resident/employee/visitor card) at scan time. Falls back to the legacy
  // per-code VQR1 personal render when my-pass is unavailable (older backend).
  // ---------------------------------------------------------------------------
  const [userPass, setUserPass] = useState<{ user_id: string; qr_secret: string } | null>(null);
  const [userPassState, setUserPassState] = useState<'loading' | 'ready' | 'none'>('loading');
  useEffect(() => {
    let alive = true;
    setUserPassState('loading');
    accessApi.qrAccess.myPass()
      .then((res) => {
        if (!alive) return;
        const d = res.data?.data;
        if (d?.user_id && d?.qr_secret) {
          setUserPass({ user_id: d.user_id, qr_secret: d.qr_secret });
          setUserPassState('ready');
        } else {
          setUserPass(null);
          setUserPassState('none');
        }
      })
      .catch(() => {
        if (alive) { setUserPass(null); setUserPassState('none'); }
      });
    return () => { alive = false; };
  }, []);

  // Pass label (what the pase grants): the active condo membership shows the
  // unit/condo it currently represents; the pass itself works at EVERY condo
  // where the user has a relationship (the scanner's condo decides).
  const passLabel = (() => {
    const activeCtx = contexts.find((c) => c.id === activeContextId);
    const condoId = activeCtx?.condoId || user?.condoId;
    const m = memberships.find((mm) => mm.condoId === condoId)
      || memberships.find((mm) => mm.condoId === (contexts.find((c) => c.kind === 'condo')?.condoId))
      || memberships[0];
    if (m?.unitLabel || m?.unitNumber) {
      return m.unitLabel || `Unidad ${m.unitNumber}`;
    }
    return m?.condoName || 'todos tus condominios';
  })();

  // (Jaime 2026-09-12) Personal QR + code are SENSITIVE: hidden by default,
  // revealed on tap (Ocultar/Mostrar). Matches the hide/show capability of the
  // former condo 'Acceso Residente' design, with a safer hidden default.
  const [personalQrRevealed, setPersonalQrRevealed] = useState(false);


  // Filter guest codes for the guest section
  const guestCodes = codes.filter(c => c.type === 'guest' || c.visitorName);
  // Archivado automático (2026-09-11): a guest code is archived when it is
  // expired AND its validity end (same computation as effectiveCodeStatus)
  // is more than 24h in the past. Recently expired (<24h) stay in the list
  // with the Expirado badge.
  const ARCHIVE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
  const archivedCodes = guestCodes.filter(c => {
    if (effectiveCodeStatus(c) !== 'expired') return false;
    const end = codeValidityEndIso(c);
    const endMs = end ? new Date(end).getTime() : NaN;
    return !Number.isNaN(endMs) && Date.now() - endMs > ARCHIVE_THRESHOLD_MS;
  });
  const archivedIds = new Set(archivedCodes.map(c => c.id));
  const visibleGuestCodes = guestCodes.filter(c => !archivedIds.has(c.id));

  // Preview of the validity window for the current form values
  const arrivalPreview = (() => {
    if (!form.arrivalDate || !form.arrivalTime) return null;
    const d = new Date(form.arrivalDate+'T'+form.arrivalTime);
    if (Number.isNaN(d.getTime())) return null;
    const fmt = (x: Date) => x.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
    // Window is +/- windowHours around arrival: start = arrival - N h, end = arrival + N h.
    return {
      from: fmt(new Date(d.getTime() - form.windowHours * 3600 * 1000)),
      until: fmt(new Date(d.getTime() + form.windowHours * 3600 * 1000)),
    };
  })();

  // --- Walk-in "Sin QR" host approvals (roadmap item E, 2026-09-16) ---
  // Pending walk-ins for the user's OWN units, with Aprobar / Rechazar.
  // Chosen entry point: MyQR (the host's access page) — the Notifications
  // center has no per-item action plumbing for walk-ins yet. Optimistic
  // update with rollback on request failure. Endpoint 404 (backend not
  // deployed) → card stays hidden, no error noise.
  const [hostWalkIns, setHostWalkIns] = useState<WalkIn[]>([]);
  const [hostWalkInsLoaded, setHostWalkInsLoaded] = useState(false);
  const [walkInBusyId, setWalkInBusyId] = useState<string | null>(null);
  const [walkInMsg, setWalkInMsg] = useState<string | null>(null);
  // Detalles toggle (bug 2026-09-18, Jaime): per-card expandable panel with
  // everything captured at the gate (photo, ID#, plate, motive, phone,
  // destination, timestamp). Missing optional fields render as dash.
  const [walkInDetailsId, setWalkInDetailsId] = useState<string | null>(null);
  // Tap-to-enlarge for the visitor ID photo (same lightbox pattern as
  // ScanResultCard).
  const [walkInPhotoZoom, setWalkInPhotoZoom] = useState<string | null>(null);
  // Back arrow (Jaime 2026-09-18, scope addition): shared smart-back —
  // navigate(-1) when an in-app back stack exists, otherwise fall back to the
  // resolved post-login destination (not a hardcoded '/').
  const goBack = useSmartBack(getPostLoginDestinationFromStorage());

  const loadHostWalkIns = useCallback(async () => {
    try {
      // FIX 2026-09-16 (B2): GET /security/walk-ins is guard-scoped (residents
      // get 403) and its rows lack unit_id, so the old client unit filter never
      // matched. Use the host-scoped endpoint GET /walk-ins/mine?status= and
      // filter client-side only on pending_approval for the approval card.
      const res = await walkInApi.mine('pending_approval');
      const all = walkInListFromResponse(res.data);
      setHostWalkIns(all.filter((w) => w.status === 'pending_approval'));
    } catch {
      // 404/403 → feature not available for this user/backend; stay hidden.
      setHostWalkIns([]);
    } finally {
      setHostWalkInsLoaded(true);
    }
  }, []);

  useEffect(() => { loadHostWalkIns(); }, [loadHostWalkIns]);

  // FIX 2026-09-16: the host approval card used to load only on mount, so a
  // resident already on /my-qr (or arriving via a notification deep-link race)
  // never saw the card until a full remount. Refresh on focus/visibility and
  // poll every 30s while mounted and the document is visible (same cadence as
  // the AppShell polling). In-flight guard avoids overlapping fetches.
  const walkInPollBusy = useRef(false);
  const refreshHostWalkInsIfIdle = useCallback(async () => {
    if (walkInPollBusy.current || document.visibilityState !== 'visible') return;
    walkInPollBusy.current = true;
    try { await loadHostWalkIns(); }
    finally { walkInPollBusy.current = false; }
  }, [loadHostWalkIns]);

  useEffect(() => {
    const onFocus = () => { void refreshHostWalkInsIfIdle(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshHostWalkInsIfIdle();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const interval = window.setInterval(() => { void refreshHostWalkInsIfIdle(); }, 30_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [refreshHostWalkInsIfIdle]);

  const decideWalkIn = useCallback(async (id: string, action: 'approve' | 'deny') => {
    const prev = hostWalkIns;
    setWalkInBusyId(id);
    // Optimistic removal with rollback on failure.
    setHostWalkIns((cur) => cur.filter((w) => w.id !== id));
    try {
      await (action === 'approve' ? walkInApi.approve(id) : walkInApi.deny(id));
      setWalkInMsg(action === 'approve' ? 'Visita aprobada — el código quedó activo.' : 'Visita rechazada.');
      setTimeout(() => setWalkInMsg(null), 3000);
      // FIX 2026-09-16: re-sync with the backend after a decision so the card
      // reflects the true server state (optimistic removal already applied).
      void loadHostWalkIns();
    } catch (err: any) {
      setHostWalkIns(prev);
      setWalkInMsg(err?.response?.data?.error || err?.response?.data?.message || 'No se pudo procesar la solicitud.');
      setTimeout(() => setWalkInMsg(null), 4000);
    } finally {
      setWalkInBusyId(null);
    }
  }, [hostWalkIns]);

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] dark:bg-slate-950 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header (back arrow added 2026-09-18, Jaime: same style as MensajesPage) */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:text-[var(--v-teal-700)] hover:bg-[var(--v-teal-400)]/10 dark:hover:bg-[var(--v-surface-1)] rounded-lg"
            title="Volver"
            aria-label="Volver a la pantalla anterior"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <QrCode className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-[var(--v-text-primary)]">Mi QR de Acceso</h1>
            <p className="text-sm text-slate-500">Tu código personal y códigos de invitados</p>
          </div>
        </div>

        {/* Notification-permission nudge (Jaime 2026-10-07): non-blocking,
            fixed bottom card; page stays fully usable while it shows. */}
        {showNotifPrompt && (
          <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 bg-[var(--v-surface-1)] dark:bg-slate-900 border border-[var(--v-border-light)] dark:border-slate-700 rounded-xl shadow-lg p-4" role="dialog" aria-label="Activar notificaciones">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-[var(--v-teal-400)] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--v-text-primary)]">Activa las notificaciones</p>
                <p className="text-sm text-[var(--v-text-secondary)] mt-0.5">Recibe avisos de tu comunidad: visitas, anuncios y más.</p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={acceptNotifications} disabled={notifBusy} className="px-3 py-1.5 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                    {notifBusy ? 'Activando…' : 'Activar'}
                  </button>
                  <button type="button" onClick={dismissNotifPrompt} className="px-3 py-1.5 rounded-lg border border-[var(--v-border-light)] text-[var(--v-text-secondary)] text-sm font-medium">
                    Ahora no
                  </button>
                </div>
              </div>
              <button type="button" onClick={dismissNotifPrompt} aria-label="Cerrar" className="text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* No-unit CTA (FIX 2026-09-16, item 5): clear free-unit creation path */}
        {noUnitMode && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-teal-50 dark:bg-teal-950/40 border border-teal-200 dark:border-teal-900 px-4 py-3 rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-semibold text-teal-800 dark:text-teal-300">Vive aquí tu hogar digital</p>
              <p className="text-sm text-teal-700 dark:text-teal-400">Crea tu unidad gratis y desbloquea tu hogar, invitados y más.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/edicion-perfil?highlight=agregar-propiedad')}
              className="shrink-0 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700"
            >
              Crear mi unidad gratis
            </button>
          </div>
        )}

        {/* Success banner */}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{successMsg}</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Walk-in "Sin QR" host approvals (roadmap item E): pending visitor
            requests for my units. Hidden entirely when none / unavailable. */}
        {hostWalkInsLoaded && hostWalkIns.length > 0 && (
          <div className="bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-xl shadow-sm border-2 border-amber-300 dark:border-amber-700 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--v-text-primary)] mb-1 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-amber-500" />
              Visitas sin QR por aprobar
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              Solicitudes registradas por seguridad para tus unidades. Tienen 15 minutos para tu respuesta.
            </p>
            {walkInMsg && (
              <div className="mb-4 text-sm rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 px-4 py-3">
                {walkInMsg}
              </div>
            )}
            <div className="space-y-3">
              {hostWalkIns.map((w) => {
                const badge = WALK_IN_STATUS_BADGE.pending_approval;
                return (
                  <div key={w.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Walk-in enhancement (Jaime 2026-09-16): visitor ID photo thumbnail.
                        Tap-to-enlarge opens the same lightbox as the detalles panel. */}
                    {w.visitor_photo && (
                      <button
                        type="button"
                        onClick={() => setWalkInPhotoZoom(w.visitor_photo || null)}
                        className="flex-shrink-0 rounded-full focus:outline-none"
                        title="Ampliar foto del ID"
                        aria-label="Ampliar foto del ID del visitante"
                      >
                        <img
                          src={w.visitor_photo}
                          alt="Foto ID del visitante"
                          className="w-14 h-14 rounded-full object-cover border border-slate-200 dark:border-slate-700 cursor-zoom-in"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 dark:text-[var(--v-text-primary)] truncate">{w.visitor_name || 'Visitante'}</p>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {w.unit_label || 'Mi unidad'}
                        {w.visitor_id_number ? ` · ID: ${w.visitor_id_number}` : ''}
                        {w.phone ? ` · ${w.phone}` : ''}
                        {w.plate ? ` · Placa ${w.plate}` : ''}
                        {w.reason ? ` · ${w.reason}` : ''}
                        {w.duration_hours ? ` · ${w.duration_hours}h` : ''}
                      </p>
                      {w.created_at && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Solicitado: {new Date(w.created_at).toLocaleString('es-MX')}
                        </p>
                      )}
                      {/* Detalles toggle (bug 2026-09-18, Jaime): expands the full
                          gate-capture payload for this request. */}
                      <button
                        type="button"
                        aria-expanded={walkInDetailsId === w.id}
                        onClick={() => setWalkInDetailsId((cur) => (cur === w.id ? null : w.id))}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {walkInDetailsId === w.id ? (
                          <>Ocultar detalles <ChevronUp className="w-3.5 h-3.5" /></>
                        ) : (
                          <>Detalles <ChevronDown className="w-3.5 h-3.5" /></>
                        )}
                      </button>
                      {walkInDetailsId === w.id && (
                        <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-3">
                          <div className="flex flex-col sm:flex-row gap-3">
                            {w.visitor_photo ? (
                              <button
                                type="button"
                                onClick={() => setWalkInPhotoZoom(w.visitor_photo || null)}
                                className="flex-shrink-0 self-start focus:outline-none"
                                title="Ampliar foto del ID"
                                aria-label="Ampliar foto del ID del visitante"
                              >
                                <img
                                  src={w.visitor_photo}
                                  alt="Documento de identificacion del visitante"
                                  className="w-28 h-28 rounded-lg object-cover border border-slate-200 dark:border-slate-700 cursor-zoom-in"
                                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                />
                              </button>
                            ) : (
                              <div className="flex-shrink-0 w-28 h-28 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-[10px] text-slate-400 text-center px-2">
                                Sin foto de ID capturada
                              </div>
                            )}
                            <dl className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Visitante</dt>
                                <dd className="text-slate-700 dark:text-slate-200 truncate">{w.visitor_name || '—'}</dd>
                              </div>
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Destino</dt>
                                <dd className="text-slate-700 dark:text-slate-200 truncate">{w.unit_label || '—'}</dd>
                              </div>
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">ID (documento)</dt>
                                <dd className="text-slate-700 dark:text-slate-200 truncate">{w.visitor_id_number || '—'}</dd>
                              </div>
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Placa</dt>
                                <dd className="text-slate-700 dark:text-slate-200 truncate">{w.plate || '—'}</dd>
                              </div>
                              <div className="min-w-0 sm:col-span-2">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Motivo</dt>
                                <dd className="text-slate-700 dark:text-slate-200 break-words">{w.reason || '—'}</dd>
                              </div>
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Teléfono</dt>
                                <dd className="text-slate-700 dark:text-slate-200 truncate">{w.phone || '—'}</dd>
                              </div>
                              <div className="min-w-0">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Duración</dt>
                                <dd className="text-slate-700 dark:text-slate-200">{w.duration_hours ? `${w.duration_hours} h` : '—'}</dd>
                              </div>
                              <div className="min-w-0 sm:col-span-2">
                                <dt className="text-[11px] uppercase tracking-wide text-slate-400">Solicitado</dt>
                                <dd className="text-slate-700 dark:text-slate-200">{w.created_at ? new Date(w.created_at).toLocaleString('es-MX') : '—'}</dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => decideWalkIn(w.id, 'approve')}
                        disabled={walkInBusyId === w.id}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                      >
                        {walkInBusyId === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Aprobar
                      </button>
                      <button
                        type="button"
                        onClick={() => decideWalkIn(w.id, 'deny')}
                        disabled={walkInBusyId === w.id}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-60 text-sm font-semibold transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Rechazar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tap-to-enlarge lightbox for the walk-in visitor ID photo
            (same overlay pattern as ScanResultCard). */}
        {walkInPhotoZoom && (
          <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
            role="dialog"
            aria-label="Documento ampliado"
            onClick={() => setWalkInPhotoZoom(null)}
          >
            <img
              src={walkInPhotoZoom}
              alt="Documento de identificacion ampliado"
              className="max-h-[85vh] max-w-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              className="absolute top-4 right-4 text-white text-3xl"
              aria-label="Cerrar"
              onClick={() => setWalkInPhotoZoom(null)}
            >×</button>
          </div>
        )}

        {/* Mi pase — VQR2 user-identity pass (ADDENDUM 2026-09-14b). Primary
            personal entry credential; the same QR resolves at any gate where
            the user has a role or a claimed invite. Sensitive: hidden by
            default, revealed on tap (same pattern as the legacy code). */}
        {userPassState !== 'none' && (
          <div className="bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-xl shadow-sm border-2 border-[var(--v-teal-500)] dark:border-[var(--v-teal-400)] p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--v-text-primary)] mb-1">Mi pase de acceso</h2>
            <p className="text-xs text-slate-500 mb-4">
              {userPassState === 'loading'
                ? 'Cargando pase…'
                : <>Un solo pase para {passLabel}. Rota cada minuto — la caseta resuelve tu acceso al escanearlo.</>}
            </p>
            {userPassState === 'loading' ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : userPass ? (
              personalQrRevealed ? (
                <RotatingAccessQr variant="vqr2" userId={userPass.user_id} code={userPass.user_id} qrSecret={userPass.qr_secret} size={240} />
              ) : (
                <button
                  type="button"
                  onClick={() => setPersonalQrRevealed(true)}
                  className="w-[240px] h-[240px] flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-600 transition-colors"
                  title="Mostrar pase"
                  aria-label="Mostrar pase de acceso personal"
                >
                  <Eye className="w-8 h-8" />
                  <span className="text-sm font-medium">Mostrar pase</span>
                </button>
              )
            ) : null}
          </div>
        )}


        {/* Generated QR Display (guest-only — hidden for employees, Jaime 2026-09-08) */}
        {!isEmployee && generatedCode && (
          <div className="bg-blue-50 dark:bg-slate-800/60 border-2 border-blue-200 dark:border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--v-text-primary)] dark:text-[var(--v-text-primary)] flex-1">Código QR de Visitante Generado</h2>
              {/* Close (2026-09-11, Jaime): explicit dismiss control for the
                  generated visitor card. Hides the card only; the code itself
                  stays in the list below (no data change). */}
              <button
                type="button"
                onClick={() => setGeneratedCode(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:bg-blue-100 dark:hover:bg-slate-700 hover:text-slate-700 transition-colors"
                title="Cerrar tarjeta"
                aria-label="Cerrar tarjeta de código generado"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-6 items-center">
              {/* QR visual — real scannable code. Phase 2 (2026-09-12): guest
                  codes with qr_secret render the ROTATING pass (VQR1 payload +
                  manual digits, 60s) via the shared RotatingAccessQr; older
                  backends without qr_secret fall back to the static code QR. */}
              {generatedCode.qr_secret ? (
                <RotatingAccessQr code={generatedCode.code} qrSecret={generatedCode.qr_secret} size={200} />
              ) : (
                <div className="p-3 bg-white border-2 border-blue-300 dark:border-slate-600 rounded-lg">
                  <img
                    src={qrImageUrl(generatedCode.code)}
                    alt="QR de acceso para visitante"
                    className="w-[200px] h-[200px]"
                  />
                </div>
              )}
              {/* Code details */}
              <div className="flex-1 w-full">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-900 dark:text-slate-100">{generatedCode.visitorName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700 dark:text-slate-200">Válido hasta: {generatedCode.validUntil ? new Date(generatedCode.validUntil).toLocaleString('es-MX') : '—'}</span>
                  </div>
                  {generatedCode.arrivalAt && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700 dark:text-slate-200">Llega: {new Date(generatedCode.arrivalAt).toLocaleString('es-MX')}{generatedCode.windowHours != null ? ` (± ${generatedCode.windowHours} h)` : ''}</span>
                    </div>
                  )}
                  {dailyRangeLine(generatedCode) && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700 dark:text-slate-200">{dailyRangeLine(generatedCode)}</span>
                    </div>
                  )}
                  {generatedCode.vehicle_plate && (
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700 dark:text-slate-200">Placa: {generatedCode.vehicle_plate}</span>
                    </div>
                  )}
                  <div className="pt-2">
                    <p className="text-xs text-slate-600 dark:text-slate-300 mb-1">Código:</p>
                    <p className="font-mono text-sm bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] px-3 py-2 rounded border border-slate-200 dark:border-slate-600 dark:text-slate-100 break-all">
                      {generatedCode.code || '—'}
                    </p>
                  </div>
                  {/* Visitor signup link (Addendum (c) §2 Case VISITOR): optional
                      unit-less profile. The backend does not yet send a signup
                      link field with the generated code, so the link is built
                      from the app origin's public /register route (see report). */}
                  <div className="pt-2 mt-2 border-t border-blue-200 dark:border-slate-700">
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 flex items-center gap-1">
                      <Link2 className="w-3 h-3" />
                      Enlace de registro para tu visitante (opcional)
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-xs bg-white dark:bg-slate-900 px-3 py-2 rounded border border-slate-200 dark:border-slate-600 dark:text-slate-100 break-all flex-1">
                        {typeof window !== 'undefined' ? window.location.origin + '/register' : '/register'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          const url = (typeof window !== 'undefined' ? window.location.origin : '') + '/register';
                          navigator.clipboard?.writeText(url);
                          setSuccessMsg('Enlace de registro copiado');
                          setTimeout(() => setSuccessMsg(null), 2000);
                        }}
                        className="px-3 py-2 border border-blue-300 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-50 flex-shrink-0"
                      >
                        Copiar
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Tu visitante puede crear un perfil de Vecindus sin vincular una unidad. Con perfil completo, seguridad ya no le pedirá identificación en el acceso.
                    </p>
                  </div>
                </div>
                {/* Guardar pase (ADDENDUM 2026-09-14, Item A): one-tap save of
                    the offline pass PNG — STATIC raw_code QR + text block.
                    Never encodes the rotating VQR1 frame (stale in 60s). */}
                <div className="mt-4">
                  <SaveVisitorPass
                    qrCode={generatedCode.code || ''}
                    pass={{
                      qrCode: generatedCode.code || '',
                      title: generatedCode.visitorName || 'Visitante',
                      lines: passLinesFor(generatedCode),
                      mapsUrl: destinationMapsUrlFor(generatedCode),
                    }}
                    dark
                  />
                </div>
                {/* Share options (2026-09-09): send the access details to the
                    visitor. Native share where available; WhatsApp / email
                    prefilled from the visitor's phone/email if provided. */}
                <div className="mt-4 pt-3 border-t border-blue-200 dark:border-slate-700">
                  <ShareAccessActions
                    onWhatsApp={() => handleWhatsAppShare(generatedCode)}
                    onEmail={() => handleEmailShare(generatedCode)}
                    onNative={() => handleNativeShare(generatedCode)}
                    onCopy={() => handleCopyShareLink(generatedCode)}
                    onSendLink={() => handleSendInviteLink(generatedCode)}
                    linkBusy={inviteBusyId === generatedCode.id}
                    linkAvailable={!inviteUnavailableIds.has(generatedCode.id)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => {
                      if (generatedCode.code) {
                        navigator.clipboard?.writeText(generatedCode.code);
                        setSuccessMsg('Código copiado');
                        setTimeout(() => setSuccessMsg(null), 2000);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Copiar código
                  </button>
                  <button
                    onClick={() => handleRevoke(generatedCode.id)}
                    className="flex items-center gap-1 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Revocar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Editar vigencia (2026-09-11): modal dialog reusing the creation-form
            fields (llegada fecha/hora + ± windowHours + rango multi-día),
            prefilled from the selected row. Submit -> PATCH validity. */}
        {editingCodeId && (() => {
          const editing = codes.find((c) => c.id === editingCodeId);
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
              onClick={() => setEditingCodeId(null)}
              role="dialog"
              aria-modal="true"
              aria-label="Editar vigencia del código"
            >
              <div
                className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    Editar vigencia{editing?.visitorName ? ` — ${editing.visitorName}` : ''}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setEditingCodeId(null)}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    title="Cerrar"
                    aria-label="Cerrar diálogo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="relative min-w-0">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="date"
                        required
                        value={editForm.date}
                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                        className={guestInputCls}
                        aria-label="Fecha de llegada"
                      />
                    </div>
                    <div className="relative min-w-0">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="time"
                        required
                        value={editForm.time}
                        onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                        className={guestInputCls}
                        aria-label="Hora de llegada"
                      />
                    </div>
                    <div className="relative min-w-0">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <select
                        value={editForm.windowHours}
                        onChange={(e) => setEditForm({ ...editForm, windowHours: Number(e.target.value) })}
                        className={guestInputCls}
                        aria-label="Ventana en horas"
                      >
                        {WINDOW_HOURS.map((h) => (
                          <option key={h} value={h}>± {h} {h === 1 ? 'hora' : 'horas'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Rango de días (opcional)
                      <span className="font-normal text-slate-500 dark:text-slate-400"> (la ventana se repite cada día del rango)</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="relative min-w-0">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                        <input
                          type="date"
                          value={editForm.validFrom}
                          onChange={(e) => setEditForm({ ...editForm, validFrom: e.target.value })}
                          className={guestInputCls}
                          aria-label="Desde"
                        />
                      </div>
                      <div className="relative min-w-0">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                        <input
                          type="date"
                          value={editForm.validUntil}
                          min={editForm.validFrom || undefined}
                          onChange={(e) => setEditForm({ ...editForm, validUntil: e.target.value })}
                          className={guestInputCls}
                          aria-label="Hasta"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Deja ambos vacíos para un código de una sola visita.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={editSubmitting}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {editSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      {editSubmitting ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingCodeId(null)}
                      className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}

        {/* Guest Codes Section — hidden entirely for employees (Jaime 2026-09-08) */}
        {!isEmployee && (
        <div className="bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 dark:border-[var(--v-surface-3)] p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-[var(--v-text-primary)] dark:text-[var(--v-text-primary)]">Códigos de invitados</h2>
            <button
              onClick={() => {
                setSelectedVisitor(null);
                if (!showForm && (!form.arrivalDate || !form.arrivalTime)) {
                  setForm({ ...form, ...defaultArrival() });
                }
                setShowForm(!showForm);
              }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Generar QR Visitante
            </button>
            <button
              onClick={() => { setShowForm(false); openKnownPicker(); }}
              className="flex items-center gap-2 border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invitar conocido
            </button>
          </div>

          {/* Known-visitor picker (Phase 2 re-invite): presence-scoped list.
              Search filters locally by name/phone — the server already limited
              the rows to this residence. */}
          {showKnown && (
            <div className="mb-6 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-blue-600" />
                  Invitar a un visitante conocido
                </h3>
                <button onClick={() => setShowKnown(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Personas ya invitadas a este condominio. Una invitación rápida no requiere volver a capturar sus datos.
              </p>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={knownQuery}
                  onChange={(e) => setKnownQuery(e.target.value)}
                  className={guestInputCls}
                  placeholder="Buscar por nombre o teléfono…"
                />
              </div>
              {knownLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              ) : knownError ? (
                <p className="text-sm text-red-600 py-2">{knownError}</p>
              ) : knownVisitors.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
                  Aún no hay visitantes conocidos en este condominio. Usa "Generar QR Visitante" para la primera invitación.
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {knownVisitors
                    .filter((v) => {
                      const q = knownQuery.trim().toLowerCase();
                      if (!q) return true;
                      return v.full_name?.toLowerCase().includes(q) || v.phone?.toLowerCase().includes(q);
                    })
                    .map((v) => (
                      <button
                        key={v.visitor_profile_id}
                        type="button"
                        onClick={() => pickKnownVisitor(v)}
                        className="w-full flex items-center gap-3 bg-white dark:bg-slate-900 rounded-lg p-3 border border-slate-200 dark:border-slate-700 text-left hover:border-blue-400 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-full bg-blue-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                          {v.photo_url ? (
                            <img src={v.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 dark:text-white truncate">{v.full_name}</p>
                          <p className="text-xs text-slate-500">{v.phone}</p>
                        </div>
                        {v.id_on_file && (
                          <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 flex-shrink-0">
                            <IdCard className="w-3 h-3" />
                            ID a bordo
                          </span>
                        )}
                        <span className="text-xs font-medium text-blue-600 flex-shrink-0">Reinvitar</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Generate Form — F67: guest_name, valid_hours 1-24, vehicle_plate */}
          {showForm && (
            <form onSubmit={handleGenerate} className="mb-6 bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 space-y-4 border border-slate-200 dark:border-slate-700">
              {/* D6 fix (2026-10-01, QA): visible indication of the condo the
                  guest code will be written to (the condo being managed). */}
              {(() => {
                const target = activeCondoId || contexts.find(c => c.kind === 'condo')?.condoId;
                if (!target) return null;
                const label = contexts.find(c => c.kind === 'condo' && c.condoId === target)?.label
                  || memberships.find((mm) => mm.condoId === target)?.condoName
                  || target;
                return (
                  <div className="flex items-center gap-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 w-fit">
                    📍 Códigos para: {label}
                  </div>
                );
              })()}
              {selectedVisitor && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                    Reinvitando a {selectedVisitor.full_name}
                  </span>
                  {selectedVisitor.id_on_file && (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                      <IdCard className="w-3 h-3" />
                      ID a bordo — no se re-escanea
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre del visitante (required) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nombre del visitante *</label>
                  <div className="relative min-w-0">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={form.guestName}
                      onChange={(e) => setForm({ ...form, guestName: e.target.value })}
                      className={guestInputCls}
                      placeholder="Ej: Juan Pérez"
                    />
                  </div>
                </div>

                {/* Llegada: fecha + hora + ventana +/- horas */}
                <div className="md:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Llegada del visitante *
                    <span className="font-normal text-slate-500 dark:text-slate-400"> (ventana de acceso en horas)</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="relative min-w-0">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="date"
                        required
                        value={form.arrivalDate}
                        onChange={(e) => setForm({ ...form, arrivalDate: e.target.value })}
                        className={guestInputCls}
                        aria-label="Fecha de llegada"
                      />
                    </div>
                    <div className="relative min-w-0">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="time"
                        required
                        value={form.arrivalTime}
                        onChange={(e) => setForm({ ...form, arrivalTime: e.target.value })}
                        className={guestInputCls}
                        aria-label="Hora de llegada"
                      />
                    </div>
                    <div className="relative min-w-0">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <select
                        value={form.windowHours}
                        onChange={(e) => setForm({ ...form, windowHours: Number(e.target.value) })}
                        className={guestInputCls}
                        aria-label="Ventana en horas"
                      >
                        {WINDOW_HOURS.map(h => (
                          <option key={h} value={h}>± {h} {h === 1 ? 'hora' : 'horas'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {arrivalPreview
                      ? <>Acceso permitido de {arrivalPreview.from} a {arrivalPreview.until}.</>
                      : 'El código será válido en la ventana alrededor de la hora de llegada.'}
                  </p>
                </div>

                {/* Rango de días (opcional, 2026-09-11): multi-day daily-repeat
                    window. Empty = single-visit code (unchanged behavior). */}
                <div className="md:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Rango de días (opcional)
                    <span className="font-normal text-slate-500 dark:text-slate-400"> (la ventana de llegada se repite cada día del rango)</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative min-w-0">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="date"
                        value={form.validFrom}
                        onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                        className={guestInputCls}
                        aria-label="Desde"
                      />
                    </div>
                    <div className="relative min-w-0">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <input
                        type="date"
                        value={form.validUntil}
                        min={form.validFrom || undefined}
                        onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                        className={guestInputCls}
                        aria-label="Hasta"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Si lo defines, el código vale de {form.validFrom || '—'} a {form.validUntil || '—'}, con la ventana de llegada repetida cada día del rango.
                  </p>
                </div>

                {/* Placa (optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Placa del vehículo (opcional)</label>
                  <div className="relative min-w-0">
                    <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={form.vehiclePlate}
                      onChange={(e) => setForm({ ...form, vehiclePlate: e.target.value.toUpperCase() })}
                      className={guestInputCls + ' uppercase'}
                      placeholder="Ej: ABC123"
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Teléfono (optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Teléfono (opcional)</label>
                  <div className="relative min-w-0">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={guestInputCls}
                      placeholder="Ej: +52 555 123 4567"
                    />
                  </div>
                </div>

                {/* Email (optional) — used to prefill the share/mailto action */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Correo del visitante (opcional)</label>
                  <div className="relative min-w-0">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={guestInputCls}
                      placeholder="Ej: juan@correo.com"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {submitting ? 'Generando...' : 'Generar Código'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setForm(emptyForm); setSelectedVisitor(null); }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Codes List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : visibleGuestCodes.length === 0 ? (
            <div className="text-center py-8">
              <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 dark:text-slate-400 text-sm">No hay códigos de invitados activos</p>
              <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">Genera un QR para permitir el acceso de un visitante</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleGuestCodes.map((code) => {
                const codeStatus = effectiveCodeStatus(code);
                return (
                <div
                  key={code.id}
                  className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 border border-slate-200 dark:border-slate-700 min-w-0"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <QrCode className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{code.visitorName}</p>
                        {code.phone && <p className="text-xs text-slate-500">{code.phone}</p>}
                        {code.vehicle_plate && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            Placa: {code.vehicle_plate}
                          </p>
                        )}
                        {(code.arrivalAt || code.arrival_at) && (
                          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Calendar className="w-3 h-3" />
                            Llega: {new Date(code.arrivalAt || code.arrival_at!).toLocaleString('es-MX')}
                            {(code.windowHours ?? code.window_hours) != null && ` (± ${code.windowHours ?? code.window_hours} h)`}
                          </div>
                        )}
                        {dailyRangeLine(code) && (
                          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Calendar className="w-3 h-3" />
                            {dailyRangeLine(code)}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 mt-1">
                          <Clock className="w-3 h-3" />
                          Válido hasta: {code.validUntil ? new Date(code.validUntil).toLocaleString('es-MX') : '—'}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 max-w-full">
                      {/* 4-state badge (2026-09-11): Permiso Activo (in-window, green),
                          Vigente (before window, blue/info), Expirado (gray), Cancelado (red). */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        codeStatus === 'active'
                          ? 'bg-green-100 text-green-700'
                          : codeStatus === 'pending'
                          ? 'bg-blue-100 text-blue-700'
                          : codeStatus === 'revoked'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-[var(--v-surface-2)] text-gray-600'
                      }`}>
                        {codeStatus === 'active'
                          ? 'Permiso Activo'
                          : codeStatus === 'pending'
                          ? 'Vigente'
                          : codeStatus === 'revoked'
                          ? 'Cancelado'
                          : 'Expirado'}
                      </span>
                      {codeStatus !== 'revoked' && (
                        <div className="flex items-center gap-1 flex-wrap justify-end">
                          {codeStatus === 'expired' && (
                            <span className="w-full text-right text-[10px] text-slate-400 dark:text-slate-500">
                              Puedes extenderlo para reactivar el acceso.
                            </span>
                          )}
                          {/* Ver tarjeta (2026-09-11): re-opens the generated
                              visitor card for this existing code (same card
                              component/state; QR re-rendered from the code
                              string). Active and Vigente (pending) only. */}
                          <button
                            onClick={() => openGeneratedCard(code)}
                            className="flex items-center gap-1 px-2 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-xs font-medium"
                            title="Ver tarjeta del código"
                          >
                            <QrCode className="w-4 h-4" />
                            Ver tarjeta
                          </button>
                          {(codeStatus === 'active' || codeStatus === 'pending') && (
                          <>
                          <button
                            onClick={() => setSharingCodeId(sharingCodeId === code.id ? null : code.id)}
                            className="flex items-center gap-1 px-2 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors text-xs font-medium"
                            title="Compartir de nuevo"
                          >
                            <Share2 className="w-4 h-4" />
                            Compartir de nuevo
                          </button>
                          {/* Cancelar (2026-09-09): revokes the invitation via
                              DELETE /api/qr-access/codes/:id — the code stops
                              validating immediately; backend semantics are a
                              hard delete, so the row is removed from the list. */}
                          <button
                            onClick={() => handleRevoke(code.id, {
                              confirmText: '¿Cancelar esta invitación? El código dejará de ser válido de inmediato.',
                              successText: 'Invitación cancelada. El código ya no es válido.',
                            })}
                            className="flex items-center gap-1 px-2 py-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors text-xs font-medium"
                            title="Cancelar invitación"
                          >
                            <Ban className="w-4 h-4" />
                            Cancelar
                          </button>
                          {codeStatus === 'active' && (
                          <button
                            onClick={() => handleRevoke(code.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Revocar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                          </>
                          )}
                          {/* Extender +N h (2026-09-11, Jaime): active, Vigente
                              (pending) and Expirado (revive via extend). PATCH
                              /api/qr-access/codes/:id/validity { extendHours }. */}
                          {[1, 2, 4].map((h) => (
                            <button
                              key={h}
                              disabled={extendBusyId === code.id}
                              onClick={() => handleExtend(code, h)}
                              className="flex items-center gap-1 px-2 py-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors text-xs font-medium disabled:opacity-50"
                              title={`Extender el acceso +${h} h`}
                            >
                              {extendBusyId === code.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : `+${h}h`}
                            </button>
                          ))}
                          {/* Editar (2026-09-11): opens the edit dialog prefilled
                              with the code's current validity; submit PATCHes
                              arrivalAt/windowHours/validFrom/validUntil. */}
                          <button
                            onClick={() => openEditDialog(code)}
                            className="flex items-center gap-1 px-2 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-xs font-medium"
                            title="Editar vigencia"
                          >
                            <Calendar className="w-4 h-4" />
                            Editar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Share panel (Compartir de nuevo): same shared component
                      used by the freshly-generated visitor card. */}
                  {sharingCodeId === code.id && (codeStatus === 'active' || codeStatus === 'pending') && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      {/* Guardar pase (ADDENDUM 2026-09-14, Item A): same
                          offline pass save as the freshly-generated card. */}
                      <div className="mb-3">
                        <SaveVisitorPass
                          qrCode={code.code || ''}
                          pass={{
                            qrCode: code.code || '',
                            title: code.visitorName || 'Visitante',
                            lines: passLinesFor(code),
                            mapsUrl: destinationMapsUrlFor(code),
                          }}
                          dark
                        />
                        {/* On-screen coordinates ARE a link (2026-09-15): the
                            saved PNG carries the maps QR instead, since a PNG
                            can't embed hyperlinks. */}
                        {destinationMapsUrlFor(code) && (
                          <a
                            href={destinationMapsUrlFor(code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex items-center justify-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            📍 Abrir ubicación en Google Maps
                          </a>
                        )}
                      </div>
                      <ShareAccessActions
                        onWhatsApp={() => handleWhatsAppShare(code)}
                        onEmail={() => handleEmailShare(code)}
                        onNative={() => handleNativeShare(code)}
                        onCopy={() => handleCopyShareLink(code)}
                        onSendLink={() => handleSendInviteLink(code)}
                        linkBusy={inviteBusyId === code.id}
                        linkAvailable={!inviteUnavailableIds.has(code.id)}
                      />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {archivedCodes.length > 0 && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowArchived(v => !v)}
                className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                <svg className={`w-3 h-3 transition-transform ${showArchived ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Archivados ({archivedCodes.length})
              </button>
              {showArchived && (
                <div className="space-y-3 mt-2">
                  {archivedCodes.map((code) => {
                    const codeStatus = effectiveCodeStatus(code);
                    return (
                    <div
                      key={code.id}
                      className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 border border-slate-200 dark:border-slate-700 opacity-80 min-w-0"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <QrCode className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{code.visitorName}</p>
                            {code.phone && <p className="text-xs text-slate-500">{code.phone}</p>}
                            {(code.arrivalAt || code.arrival_at) && (
                              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                <Calendar className="w-3 h-3" />
                                Llega: {new Date(code.arrivalAt || code.arrival_at!).toLocaleString('es-MX')}
                                {(code.windowHours ?? code.window_hours) != null && ` (± ${code.windowHours ?? code.window_hours} h)`}
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 mt-1">
                              <Clock className="w-3 h-3" />
                              Válido hasta: {code.validUntil ? new Date(code.validUntil).toLocaleString('es-MX') : '—'}
                            </div>
                          </div>
                        </div>
                        {/* Archived (2026-09-11): expired stays "Expirado"; revoked now "Cancelado" */}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          codeStatus === 'revoked' ? 'bg-red-100 text-red-700' : 'bg-[var(--v-surface-2)] text-gray-600'
                        }`}>
                          {codeStatus === 'revoked' ? 'Cancelado' : 'Expirado'}
                        </span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
